import { Component, inject, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Import the reusable component
import { HandleConsentSimplifiedComponent } from '@components/shared/handle-consent-simplified/handle-consent-simplified.component';

// Import the SDK service
import { SdkManagerService } from '@services/sdk-manager.service';

@Component({
  selector: 'app-sdk-handle-consent',
  standalone: true,
  imports: [
    CommonModule,
    HandleConsentSimplifiedComponent
  ],
  template: `
    <div class="sdk-handle-consent-container">
      <app-handle-consent-simplified 
        [consentRequestId]="consentRequestId()"
        (complete)="onConsentComplete($event)">
      </app-handle-consent-simplified>
    </div>
  `,
  styles: [`
    .sdk-handle-consent-container {
      width: 100%;
      height: calc(100% - 120px);
      
      app-handle-consent-simplified {
        display: block;
        width: 100%;
        height: 100%;
      }
    }
  `]
})
export class SdkHandleConsentComponent {
  
  // Injected services
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sdkManagerService = inject(SdkManagerService);
  
  // Signal to hold the consent request ID from route params
  readonly consentRequestId = signal<string | null>(null);
  
  constructor() {
    // Setup route monitoring to extract consentRequestId
    this.route.queryParamMap.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((params: ParamMap) => {
      const requestId = params.get('consentRequestId');
      this.consentRequestId.set(requestId);
    });
  }

  /**
   * Handle completion event from the consent component
   * Terminate the SDK session with the provided status and message
   */
  onConsentComplete({status, message}: {status: 'SUCCESS' | 'FAILURE', message: string}): void {
    this.sdkManagerService.messageAndTerminateSession({
      status, 
      message
    }, false);
  }
}
